// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { getImplementedTraits } from '@tectonics/look-and-feel';
import laf from '@tectonics/look-and-feel/shopify';
import { useCurrentAllFeatures } from '@tectonics/prs/payment-spec';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { RadioButton, Separator, Label, Icon, RoundBox, VerticalLabel } from '../../../../../../commons/atoms';
import { ErrorLabel } from '../../../form/commons';
import { getSafeBankIconSrc } from './get-bank-icon-src';
import { compareBankOptionsOrder } from '@tectonics/prs/argentina';

export function FieldArgentineCashBankList(): Node {
  const allFeatures = useCurrentAllFeatures();
  const [selectedBankCode, setSelectedBankCode] = useUserInputValue('selectedBankCode');
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('selectedBankCode');
  const submitting = useSubmitting();
  const inputId = useUniqueId();

  const bankList = [
    ...getImplementedTraits(laf, 'field_argentine_cash_bank_list',  allFeatures),
  ].sort(compareBankOptionsOrder);

  return (
    <div style={{ gridColumn: '1/3'  }}  data-test-name="payment-type-code">
      <Label as="span">Selecciona el método de pago</Label>

      <RoundBox style={{ display: 'flex', justifyContent: 'space-around'}}>
        {bankList.map(value => (
          <VerticalLabel
            htmlFor={`${inputId}-${value}`}
            key={value}
            style={{backgroundColor: value === selectedBankCode ? 'white' : ''}}
          >
            <Icon src={getSafeBankIconSrc(value)} alt={value} />
            <Separator direction="vertical" size={2}/>
            <span>{getCashOptionName(value)}</span>
            <Separator direction="vertical" size={3}/>
            <RadioButton
              id={`${inputId}-${value}`}
              name="payment_type_code"
              value={value}
              checked={value === selectedBankCode}
              onChange={(e) => setSelectedBankCode(e.target.value)}
              disabled={submitting}
              data-error={formHasBeenSent && validationError ? 'error' : null}
              data-test-value={value}
            />
          </VerticalLabel>
        ))}
      </RoundBox>

      {formHasBeenSent && validationError && (
        <ValidationErrorMessage validationError={validationError} />
      )}
    </div>
  );
}

type ValidationErrorMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationErrorMessage({ validationError }: ValidationErrorMessageProps): Node {
  switch (validationError.id) {
    case 'empty':
      return (
        <ErrorLabel>
          Selecciona el método de pago
        </ErrorLabel>
      );

    case 'server-error':
      return (
        <ErrorLabel>
          {serverErrorMessage(validationError.payload)}
        </ErrorLabel>
      );

    default:
      return <ErrorLabel>Invalido</ErrorLabel>;
  }
}

function getCashOptionName(code: string): string {
  switch (code) {
    case 'pagofacil':
      return 'Pagofacil';
    case 'rapipago':
      return 'Rapipago';
    default:
      throw new Error(`Cash option "${code}" not supported`);
  }
}
